import React, { useState, useRef, useEffect, useId } from "react";
import toWav from "audiobuffer-to-wav";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugin/wavesurfer.regions";
import CursorPlugin from "wavesurfer.js/src/plugin/cursor";
import TimelinePlugin from "wavesurfer.js/dist/plugin/wavesurfer.timeline";
import SpectrogramPlugin from "wavesurfer.js/dist/plugin/wavesurfer.spectrogram";

import { CiPlay1, CiPause1, CiVolumeHigh, CiVolumeMute, CiZoomIn, CiZoomOut, CiTrash } from "react-icons/ci";
import { RiScissorsCutLine } from "react-icons/ri";
import { BsArrowCounterclockwise, BsArrowClockwise, BsSkipBackward, BsSkipForward, BsStopFill, BsRepeat } from "react-icons/bs";
import { IoAlertCircleOutline } from "react-icons/io5";
import Button from "../UI/Button";
import { ButtonTypes } from "../../utils/Constants";
import AudioPlayer from "../UI/AudioPlayer";
import { toast } from "../../utils/toast";
import {
  Channels,
  channelLength,
  computePeaks,
  deleteRange,
  keepRange,
  normalizePeak,
  resampleForSpeed,
  scaleChannels,
  shiftAudio,
} from "../../utils/audioEdits";
import { EFFECT_PRESETS, EffectKind, EffectSettings, renderEffect } from "../../utils/audioEffects";

interface AudioEditorProps {
  file: File | null;
  audioContext: AudioContext;
  onAudioEdited: (editedFile: File) => void;
  mainAudioCurrentTime: number;
  passEditedAudioRef: (ref: React.RefObject<WaveSurfer>) => void;
  handlePlayTogether: () => void;
  reset: () => void;
  isSaving?: boolean;
  // Optional, so older call sites keep working:
  pauseMain?: () => void; // pauses the main track when "Play with main track" is on
  onSubmitOriginal?: () => void; // shows "Add original audio" (adding a new audio)
  onCancel?: () => void;
  submitLabel?: string;
  mainTrackUrl?: string; // drawn in the "Main track" lane, on the same timeline as your audio
  sectionName?: string; // e.g. "Keys", shown next to "Your audio"
}

// "Main track" lane: the main track's waveform, drawn on exactly the same
// time window as your audio (it follows zoom and scrolling), so you can see
// whether the two line up.
type MainLaneState =
  | { status: "idle" | "loading" | "error" }
  | { status: "ready"; peaks: Float32Array; peaksPerSecond: number; duration: number };
const MAIN_LANE_PEAKS_PER_SECOND = 200;

type Tab = "edit" | "align" | "levels" | "effects";
const TABS: { id: Tab; label: string }[] = [
  { id: "edit", label: "Edit" },
  { id: "align", label: "Align" },
  { id: "levels", label: "Levels" },
  { id: "effects", label: "Effects" },
];

// One entry per undoable edit. The nudge total is stored alongside the
// buffer so undo/redo also restore the "Moved by" readout.
interface HistoryEntry {
  buffer: AudioBuffer;
  shiftMs: number;
}

type FxSettingsState = { [K in EffectKind]: Extract<EffectSettings, { kind: K }> };

const EFFECT_INFO: { kind: EffectKind; title: string; description: string }[] = [
  { kind: "rumble", title: "Remove rumble", description: "Cuts very low sounds such as desk thumps, traffic and hum." },
  { kind: "eq", title: "EQ", description: "Shape the tone: lows, mids and highs." },
  { kind: "compressor", title: "Compressor", description: "Evens out loud and soft notes." },
  { kind: "reverb", title: "Reverb", description: "Puts a dry recording in a room or hall." },
  { kind: "echo", title: "Echo", description: "Repeats the sound after a short delay." },
  { kind: "gate", title: "Noise gate", description: "Silences the quiet background between phrases." },
];

const formatClock = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s < 10 ? "0" : ""}${s.toFixed(1)}`;
};

const toChannels = (buf: AudioBuffer): Channels =>
  Array.from({ length: buf.numberOfChannels }, (_, ch) => buf.getChannelData(ch).slice());

const AudioEditor: React.FC<AudioEditorProps> = ({
  file,
  audioContext,
  onAudioEdited,
  mainAudioCurrentTime,
  passEditedAudioRef,
  handlePlayTogether,
  reset,
  isSaving,
  pauseMain,
  onSubmitOriginal,
  onCancel,
  submitLabel = "Save edited audio",
  mainTrackUrl,
  sectionName,
}) => {
  const audioRefOriginal = useRef<HTMLAudioElement | null>(null);
  const [mainLane, setMainLane] = useState<MainLaneState>({ status: mainTrackUrl ? "loading" : "idle" });
  const mainCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Wavesurfer's event handlers are set up once, so they call the latest
  // drawing function through this ref.
  const drawMainLaneRef = useRef<() => void>(() => { });
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const [startTrim, setStartTrim] = useState(0);
  const [endTrim, setEndTrim] = useState(0);

  const waveformRef = useRef<HTMLDivElement | null>(null);
  const [wavesurfer, setWavesurfer] = useState<any>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const lastPositionRef = useRef(0);
  const [zoom, setZoom] = useState(0);
  const [showRegion, setShowRegion] = useState(false);
  const [loopRegion, setLoopRegion] = useState(false);

  const [fadeInSec, setFadeInSec] = useState<number>(0.5);
  const [fadeOutSec, setFadeOutSec] = useState<number>(0.5);

  const [pan, setPan] = useState(0);

  const spectrogramRef = useRef<HTMLDivElement | null>(null);

  const [preSilenceSec, setPreSilenceSec] = useState<number>(0);
  const [postSilenceSec, setPostSilenceSec] = useState<number>(0);
  const [silenceApplied, setSilenceApplied] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<Tab>("edit");
  const [withMain, setWithMain] = useState(false);
  const [shiftMs, setShiftMsState] = useState(0);
  const shiftMsRef = useRef(0);
  const setShift = (ms: number) => {
    shiftMsRef.current = ms;
    setShiftMsState(ms);
  };

  const [fxSettings, setFxSettings] = useState<FxSettingsState>(() => ({
    rumble: EFFECT_PRESETS.rumble[0].settings,
    eq: EFFECT_PRESETS.eq[0].settings,
    compressor: EFFECT_PRESETS.compressor[0].settings,
    reverb: EFFECT_PRESETS.reverb[0].settings,
    echo: EFFECT_PRESETS.echo[0].settings,
    gate: EFFECT_PRESETS.gate[0].settings,
  }));
  // While an effect is being previewed, the waveform shows either the
  // original ("A") or the processed version ("B"); nothing is committed to
  // the history until Apply.
  const [fxPreview, setFxPreview] = useState<{
    kind: EffectKind;
    settings: EffectSettings;
    original: AudioBuffer;
    processed: AudioBuffer;
    showing: "A" | "B";
  } | null>(null);
  const [fxBusy, setFxBusy] = useState<EffectKind | null>(null);

  const baseId = useId();
  const fieldId = (name: string) => `${baseId}-${name}`;

  // Remembers the volume level from just before muting, so unmuting restores
  // it instead of always jumping to 100%.
  const previousVolumeRef = useRef(1);

  // Undo/redo history for destructive edits (trim, reverse, fades, silence,
  // normalize, nudge, effects). Kept as a capped stack of full buffers rather
  // than diffs — simple and safe for the short clips this editor deals with.
  const MAX_HISTORY = 20;
  const undoStackRef = useRef<HistoryEntry[]>([]);
  const redoStackRef = useRef<HistoryEntry[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  useEffect(() => {
    if (file) {
      loadAudioFile(file);
    }
  }, [file]);

  useEffect(() => {
    if (wavesurfer) {
      passEditedAudioRef(wavesurfer);
    }
  }, [wavesurfer, passEditedAudioRef]);

  function generateGradientSteps() {
    const colors: number[][] = [];
    for (let i = 0; i < 256; i++) {
      const t = i / 255;
      // interpolate from purple (128,0,128) to orange (255,165,0)
      const r = 0.5 + 0.5 * t; // from 0.5 to 1
      const g = 0.0 + 0.65 * t; // from 0 to ~0.65
      const b = 0.5 * (1 - t); // from 0.5 to 0
      colors.push([r, g, b, 1]);
    }
    return colors;
  }

  useEffect(() => {
    if (!wavesurfer || !wavesurfer.backend.ac) return;
    const panNode = wavesurfer.backend.ac.createStereoPanner();
    panNode.pan.setValueAtTime(pan, wavesurfer.backend.ac.currentTime);
    wavesurfer.backend.setFilter(panNode);

    return () => {
      panNode.disconnect();
    };
  }, [wavesurfer, pan]);

  useEffect(() => {
    if (wavesurfer || !waveformRef.current) return;

    // Typed loosely like `wavesurfer` state: the local type file for v6
    // doesn't list everything the library has (e.g. getCurrentTime).
    const ws: any = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#b7c2b1",
      progressColor: "#273f29",
      cursorColor: "#e0312b",
      height: 110,
      barWidth: 2,
      responsive: true,
      scrollParent: true,
      autoCenter: true,
      plugins: [
        RegionsPlugin.create(),
        CursorPlugin.create({
          showTime: true,
          opacity: "1",
        }),
        TimelinePlugin.create({
          container: timelineRef.current,
          primaryColor: "#526554",
          secondaryColor: "#c9cfc4",
          primaryFontColor: "#555555",
          secondaryFontColor: "#999999",
          fontSize: 11,
          // "0:05" / "1:30" instead of bare seconds
          formatTimeCallback: (sec: number) => {
            const m = Math.floor(sec / 60);
            const s = Math.round(sec - m * 60);
            return `${m}:${s < 10 ? "0" : ""}${s}`;
          },
          height: 20,
        }),
        //  EnvelopePlugin.create({ height: 50, color: "rgba(0,150,0,0.5)" }),

        SpectrogramPlugin.create({
          container: spectrogramRef.current,
          labels: true,
          height: 240,
          // splitChannels: true,
          // Frequency scale type:
          // - 'linear': Standard linear frequency scale (0-20kHz)
          // - 'logarithmic': Logarithmic scale, better for low frequencies
          // - 'mel': Mel scale based on human hearing perception (default)
          // - 'bark': Bark scale for psychoacoustic analysis
          // - 'erb': ERB scale for auditory filter modeling
          scale: 'mel',
          frequencyMax: 8000, // Maximum frequency to show (in Hz)
          frequencyMin: 0, // Minimum frequency to show (in Hz)
          fftSamples: 1024,
          labelsBackground: 'rgba(0, 0, 0, 0.1)',
          useWebWorker: true,
          // Window function for FFT (affects frequency resolution vs time resolution):
          // windowFunc: 'hann' | 'hamming' | 'blackman' | 'bartlett' | 'cosine' | 'gauss' | 'lanczoz' | 'rectangular' | 'triangular'
          // gainDB: 20,
          // rangeDB: 80,
          // maxCanvasWidth: 30000,
          // colorMap: 'roseus'
          colorMap: generateGradientSteps(),
        }),
      ],
    });

    const redrawMainLane = () => drawMainLaneRef.current();

    ws.on("ready", () => {
      ws.enableDragSelection({ color: "rgba(39, 63, 41, 0.15)" });
      setDuration(ws.getDuration());
      setPosition(ws.getCurrentTime());
      redrawMainLane();
    });

    // Keep the "Main track" lane on the same time window as your audio.
    ws.on("scroll", redrawMainLane);
    ws.on("zoom", redrawMainLane);
    ws.on("redraw", redrawMainLane);

    ws.on("play", () => setIsPlaying(true));
    ws.on("pause", () => setIsPlaying(false));
    ws.on("finish", () => setIsPlaying(false));

    // The playhead readout only needs ~10 updates a second; updating on
    // every audio frame would re-render the whole editor constantly.
    ws.on("audioprocess", (t: number) => {
      if (Math.abs(t - lastPositionRef.current) >= 0.1) {
        lastPositionRef.current = t;
        setPosition(t);
      }
    });
    ws.on("seek", () => {
      lastPositionRef.current = ws.getCurrentTime();
      setPosition(lastPositionRef.current);
    });

    ws.on("region-update-end", (region: any) => {
      setStartTrim(region.start);
      setEndTrim(region.end);
    });

    // Only one trim region should ever exist. Previously this removed
    // whichever region happened to be first in insertion order once a
    // second one appeared — since the button-created region ('main') was
    // always created first, dragging a new region directly on the waveform
    // silently deleted 'main' instead of the stray one, and the Start/End/
    // Trim controls (which look up 'main' specifically) quietly stopped
    // working. Removing every *other* region when a new one is created
    // fixes that regardless of which region came first, and also means a
    // drag-created region is properly tracked (it wasn't before).
    ws.on("region-created", (region: any) => {
      const regions = region.wavesurfer.regions.list;
      Object.keys(regions).forEach((key) => {
        if (regions[key] !== region) regions[key].remove();
      });
      setStartTrim(region.start);
      setEndTrim(region.end);
      setShowRegion(true);
    });

    // ws.setPlaybackRate(speed);
    // ws.setVolume(volume);

    setWavesurfer(ws);
    return () => ws.destroy();
  }, []);

  useEffect(() => {
    if (!file || !wavesurfer) return;

    if (wavesurfer.loadBlob) {
      wavesurfer.loadBlob(file);
      return;
    }

    const url = URL.createObjectURL(file);
    wavesurfer.load(url);
    return () => URL.revokeObjectURL(url);
  }, [file, wavesurfer]);

  // Load the main track once and keep only a small peaks overview of it.
  // The file is on the media server, so the browser can only read it when
  // that server allows it (CORS). It does locally; if it doesn't, the lane
  // shows a short note and everything else keeps working.
  useEffect(() => {
    if (!mainTrackUrl) {
      setMainLane({ status: "idle" });
      return;
    }
    const OfflineCtx = window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
    if (!OfflineCtx) {
      setMainLane({ status: "error" });
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    setMainLane({ status: "loading" });
    (async () => {
      try {
        const response = await fetch(mainTrackUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.arrayBuffer();
        // A throwaway context only for decoding; 22.05 kHz is plenty for an overview.
        const decoded: AudioBuffer = await new OfflineCtx(1, 1, 22050).decodeAudioData(data);
        if (cancelled) return;
        const channels = Array.from({ length: decoded.numberOfChannels }, (_, ch) => decoded.getChannelData(ch));
        setMainLane({
          status: "ready",
          peaks: computePeaks(channels, decoded.sampleRate, MAIN_LANE_PEAKS_PER_SECOND),
          peaksPerSecond: MAIN_LANE_PEAKS_PER_SECOND,
          duration: decoded.duration,
        });
      } catch (error) {
        if (cancelled) return;
        console.error("Main track waveform unavailable:", error);
        setMainLane({ status: "error" });
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [mainTrackUrl]);

  drawMainLaneRef.current = () => {
    const canvas = mainCanvasRef.current;
    if (!canvas) return;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    if (!cssWidth || !cssHeight) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(cssWidth * dpr) || canvas.height !== Math.round(cssHeight * dpr)) {
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
    }
    const ctx2d = canvas.getContext("2d");
    if (!ctx2d) return;
    ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx2d.clearRect(0, 0, cssWidth, cssHeight);
    if (mainLane.status !== "ready") return;

    const { peaks, peaksPerSecond, duration: mainDuration } = mainLane;
    // The time window your audio's waveform shows right now (it scrolls
    // when zoomed in); before your audio is loaded, the whole main track.
    let t0 = 0;
    let t1 = mainDuration;
    const wrapper = wavesurfer?.drawer?.wrapper as HTMLElement | undefined;
    const takeDuration = wavesurfer?.getDuration?.() || 0;
    if (wrapper && takeDuration > 0 && wrapper.scrollWidth > 0) {
      t0 = (wrapper.scrollLeft / wrapper.scrollWidth) * takeDuration;
      t1 = ((wrapper.scrollLeft + wrapper.clientWidth) / wrapper.scrollWidth) * takeDuration;
    }
    const span = Math.max(1e-6, t1 - t0);
    const step = 3; // 2px bars with 1px gaps, like the waveform below
    for (let x = 0; x < cssWidth; x += step) {
      const ta = t0 + (x / cssWidth) * span;
      if (ta >= mainDuration) break; // main track ends before your audio does
      const tb = t0 + ((x + step) / cssWidth) * span;
      const i0 = Math.floor(ta * peaksPerSecond);
      const i1 = Math.min(peaks.length, Math.max(i0 + 1, Math.ceil(tb * peaksPerSecond)));
      let peak = 0;
      for (let i = i0; i < i1; i++) if (peaks[i] > peak) peak = peaks[i];
      const h = Math.max(1, peak * (cssHeight - 4));
      ctx2d.fillStyle = ta < mainAudioCurrentTime ? "#8f9c8f" : "#c9cfc4";
      ctx2d.fillRect(x, (cssHeight - h) / 2, 2, h);
    }
    if (mainAudioCurrentTime > 0 && mainAudioCurrentTime >= t0 && mainAudioCurrentTime <= t1) {
      ctx2d.fillStyle = "#e0312b";
      ctx2d.fillRect(((mainAudioCurrentTime - t0) / span) * cssWidth - 1, 0, 2, cssHeight);
    }
  };

  // Redraw after every render (cheap: it only reads the small peaks array),
  // which covers new peaks, the main track's playhead and duration changes.
  useEffect(() => {
    drawMainLaneRef.current();
  });

  useEffect(() => {
    const canvas = mainCanvasRef.current;
    if (!canvas || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => drawMainLaneRef.current());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [mainTrackUrl]);

  // Clicking the main track lane jumps your audio to that moment.
  const handleMainLaneClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!wavesurfer) return;
    const wrapper = wavesurfer.drawer?.wrapper as HTMLElement | undefined;
    const takeDuration = wavesurfer.getDuration();
    if (!wrapper || !takeDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max(0, e.clientX - rect.left), rect.width);
    const t0 = (wrapper.scrollLeft / wrapper.scrollWidth) * takeDuration;
    const t1 = ((wrapper.scrollLeft + wrapper.clientWidth) / wrapper.scrollWidth) * takeDuration;
    seekToTime(t0 + (x / rect.width) * (t1 - t0));
  };

  //--------------------------
  useEffect(() => {
    if (!wavesurfer || !spectrogramRef.current) return;

    const spectrogramElement = spectrogramRef.current;

    const handleSpectrogramClick = (event: MouseEvent) => {
      const spectrogramWidth = spectrogramElement.offsetWidth;
      const clickPosition = event.offsetX;
      const duration = wavesurfer.getDuration();
      if (!spectrogramWidth || !duration) return;

      const time = (clickPosition / spectrogramWidth) * duration;
      wavesurfer.seekTo(Math.min(1, Math.max(0, time / duration)));
    };

    spectrogramElement.addEventListener("click", handleSpectrogramClick);

    return () => {
      spectrogramElement.removeEventListener("click", handleSpectrogramClick);
    };
  }, [wavesurfer]);
  //-----------------------------

  useEffect(() => {
    if (wavesurfer) wavesurfer.zoom(zoom);
  }, [zoom, wavesurfer]);

  useEffect(() => {
    if (!wavesurfer) return;

    const region = getRegion();
    if (region) {
      region.update({ loop: loopRegion });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loopRegion, wavesurfer, showRegion]);

  const loadAudioFile = async (file: File) => {
    const audio = audioRefOriginal.current;
    if (!audio) return;

    audio.src = URL.createObjectURL(file);

    // MediaRecorder output (recorded audio, typically webm/opus) is a
    // streaming-oriented container that usually doesn't carry its overall
    // duration in the header, so Chrome/Chromium reports `duration` as
    // Infinity until the browser has actually seeked through the file —
    // which is why the "Original audio" player showed 0:00 for a recording
    // (a regular uploaded file has real duration metadata and isn't
    // affected). Seeking near the end once forces it to compute the real
    // duration, then we seek back to the start.
    const fixStreamingDuration = () => {
      if (audio.duration === Infinity || Number.isNaN(audio.duration)) {
        audio.currentTime = 1e101;
        audio.addEventListener(
          "timeupdate",
          () => { audio.currentTime = 0; },
          { once: true }
        );
      }
    };
    audio.addEventListener("loadedmetadata", fixStreamingDuration, { once: true });
  };

  const seekToTime = (time: number) => {
    if (!wavesurfer) return;
    const total = wavesurfer.getDuration();
    if (!total) return;
    wavesurfer.seekTo(Math.min(1, Math.max(0, time / total)));
  };

  const handleBack = () => {
    if (!wavesurfer) return;
    seekToTime(Math.max(wavesurfer.getCurrentTime() - 10, 0));
  };

  const handleForward = () => {
    if (!wavesurfer) return;
    seekToTime(Math.min(wavesurfer.getCurrentTime() + 10, wavesurfer.getDuration()));
  };

  const handleSpeedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSpeed = parseFloat(event.target.value);
    setSpeed(newSpeed);

    if (wavesurfer) {
      wavesurfer.setPlaybackRate(newSpeed);
    }
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    if (newVolume > 0) previousVolumeRef.current = newVolume;

    if (wavesurfer) {
      wavesurfer.setVolume(newVolume);
    }
  };

  const toggleMute = () => {
    // Previously this always snapped to exactly 0 or 1, so unmuting jumped
    // to 100% instead of restoring whatever level was set before muting.
    const newVolume = volume === 0 ? (previousVolumeRef.current || 1) : 0;
    if (volume !== 0) previousVolumeRef.current = volume;
    setVolume(newVolume);
    if (wavesurfer) wavesurfer.setVolume(newVolume);
  };

  const toBuffer = (channels: Channels, sampleRate: number): AudioBuffer => {
    const out = audioContext.createBuffer(channels.length, Math.max(1, channelLength(channels)), sampleRate);
    // .set() instead of copyToChannel(): same copy, but newer TypeScript
    // versions (e.g. the one in VS Code) don't reject it.
    channels.forEach((data, ch) => out.getChannelData(ch).set(data.subarray(0, out.length)));
    return out;
  };

  const clearRegion = () => {
    const region = getRegion();
    if (region) region.remove();
    setStartTrim(0);
    setEndTrim(0);
    setShowRegion(false);
  };

  // "Keep selection": keep only the selected region. Uses whole-sample
  // positions and a few-millisecond fade at the cut so it doesn't click.
  const trimWaveform = () => {
    if (!wavesurfer || fxPreview) return;
    const region = getRegion();
    const buf = getActiveBuffer();
    if (!region || !buf) return;

    pushHistory();
    const out = toBuffer(keepRange(toChannels(buf), buf.sampleRate, region.start, region.end), buf.sampleRate);
    clearRegion();
    loadBufferIntoEverything(out);
  };

  // The other kind of "trim": cut the selected range out and splice the
  // remaining before/after pieces together (e.g. remove 0:09–0:12 from a
  // 1:45 track), instead of trimWaveform's keep-only-the-selection.
  // (The previous version used fractional sample positions, so for about
  // half of all dragged selections the joined pieces didn't fit the new
  // buffer and it threw.)
  const deleteSelectedRegion = () => {
    if (!wavesurfer || fxPreview) return;
    const region = getRegion();
    const buf = getActiveBuffer();
    if (!region || !buf) return;

    pushHistory();
    const out = toBuffer(deleteRange(toChannels(buf), buf.sampleRate, region.start, region.end), buf.sampleRate);
    clearRegion();
    loadBufferIntoEverything(out);
  };

  const toggleRegion = () => {
    if (!wavesurfer) return;

    if (showRegion) {
      // Remove the existing region and reset trim values when hiding it
      clearRegion();
    } else {
      const duration = wavesurfer.getDuration();
      // wavesurfer.addRegion() fires "region-created" synchronously, and
      // that handler already sets startTrim/endTrim/showRegion — setting
      // them again here used to race with it: since it set showRegion to
      // true first, the unconditional toggle below it (`prev => !prev`)
      // flipped it straight back to false, so the region was visible on
      // the waveform but the "Trim waveform" button (gated on showRegion)
      // never appeared. Let the handler own that state instead.
      wavesurfer.addRegion({
        id: 'main',
        start: Math.max(0, duration / 2 - duration / 5), // start of region
        end: duration / 2, // end of region
        color: "rgba(39, 63, 41, 0.15)", // region color
        loop: loopRegion,
      });
    }
  };

  const resetTrimAndRegion = () => {
    if (wavesurfer) {
      clearRegion();

      // Reloading the original file makes any prior undo/redo history
      // (buffers derived from edits) meaningless, so clear it too.
      undoStackRef.current = [];
      redoStackRef.current = [];
      setCanUndo(false);
      setCanRedo(false);
      setShift(0);
      setFxPreview(null);

      if (file) {
        wavesurfer.load(URL.createObjectURL(file));
      }
    }
  };

  const handleTrimChange = (type: 'start' | 'end', value: number) => {
    if (!wavesurfer || !Number.isFinite(value)) return;

    const region = getRegion();

    if (region) {
      // Update the region's start or end based on the input change
      if (type === 'start') {
        region.update({ start: Math.max(0, value), end: Math.max(region.end, value) }); // Prevent setting start > end
        setStartTrim(Math.max(0, value));
      } else {
        region.update({ end: Math.max(region.start, value) }); // Prevent setting end < start
        setEndTrim(Math.max(0, value));
      }
    }
  };

  // ---------- Helpers to update WS ----------
  const loadBufferIntoEverything = (newBuffer: AudioBuffer) => {
    if (wavesurfer) {
      wavesurfer.stop();
      wavesurfer.loadDecodedBuffer(newBuffer);
      // keep paused & restore params
      if (typeof wavesurfer.pause === "function") wavesurfer.pause();
      if (typeof wavesurfer.seekTo === "function") wavesurfer.seekTo(0);
      wavesurfer.setVolume(volume);
      wavesurfer.setPlaybackRate(speed);
    }
  };

  // Like loadBufferIntoEverything, but keeps the playhead (and playback)
  // where it was — used to switch between A and B while previewing.
  const swapBuffer = (newBuffer: AudioBuffer) => {
    if (!wavesurfer) return;
    const wasPlaying = wavesurfer.isPlaying();
    const t = wavesurfer.getCurrentTime();
    wavesurfer.stop();
    wavesurfer.loadDecodedBuffer(newBuffer);
    wavesurfer.setVolume(volume);
    wavesurfer.setPlaybackRate(speed);
    seekToTime(t);
    if (wasPlaying) wavesurfer.play();
  };

  const getActiveBuffer = (): AudioBuffer | null => {
    if (wavesurfer?.backend?.buffer)
      return wavesurfer.backend.buffer as AudioBuffer;
    return null;
  };

  // There is ever only one trim region (the "region-created" handler above
  // removes any other one), so grabbing whichever region currently exists
  // is always correct — whether it was created via "Select a region" or
  // by dragging directly on the waveform.
  const getRegion = (): any => {
    if (!wavesurfer) return null;
    const regions = wavesurfer.regions.list;
    const key = Object.keys(regions)[0];
    return key ? regions[key] : null;
  };

  // -----------------------------------------------------------------

  const commitHistoryEntry = (entry: HistoryEntry) => {
    undoStackRef.current.push(entry);
    if (undoStackRef.current.length > MAX_HISTORY) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  };

  const pushHistory = () => {
    const current = getActiveBuffer();
    if (!current) return;
    commitHistoryEntry({ buffer: current, shiftMs: shiftMsRef.current });
  };

  const undo = () => {
    if (fxPreview) return;
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    const current = getActiveBuffer();
    if (current) redoStackRef.current.push({ buffer: current, shiftMs: shiftMsRef.current });
    loadBufferIntoEverything(previous.buffer);
    setShift(previous.shiftMs);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
  };

  const redo = () => {
    if (fxPreview) return;
    const next = redoStackRef.current.pop();
    if (!next) return;
    const current = getActiveBuffer();
    if (current) undoStackRef.current.push({ buffer: current, shiftMs: shiftMsRef.current });
    loadBufferIntoEverything(next.buffer);
    setShift(next.shiftMs);
    setCanRedo(redoStackRef.current.length > 0);
    setCanUndo(true);
  };
  // -----------------------------------------------------------------

  // -------------- Extra Edits (Web Audio API) --------------
  const applyFadeIn = () => {
    const buf = getActiveBuffer();
    if (!buf || fxPreview) return;
    pushHistory();
    const out = audioContext.createBuffer(
      buf.numberOfChannels,
      buf.length,
      buf.sampleRate
    );
    const fadeSamples = Math.min(
      buf.length,
      Math.floor(fadeInSec * buf.sampleRate)
    );
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
      const src = buf.getChannelData(ch);
      const dst = out.getChannelData(ch);
      for (let i = 0; i < buf.length; i++) {
        const gain = i < fadeSamples ? i / fadeSamples : 1;
        dst[i] = src[i] * gain;
      }
    }
    loadBufferIntoEverything(out);
  };

  const applyFadeOut = () => {
    const buf = getActiveBuffer();
    if (!buf || fxPreview) return;
    pushHistory();
    const out = audioContext.createBuffer(
      buf.numberOfChannels,
      buf.length,
      buf.sampleRate
    );
    const fadeSamples = Math.min(
      buf.length,
      Math.floor(fadeOutSec * buf.sampleRate)
    );
    const start = buf.length - fadeSamples;
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
      const src = buf.getChannelData(ch);
      const dst = out.getChannelData(ch);
      for (let i = 0; i < buf.length; i++) {
        let gain = 1;
        if (i >= start) {
          const t = (i - start) / Math.max(1, fadeSamples);
          gain = 1 - t;
        }
        dst[i] = src[i] * Math.max(0, gain);
      }
    }
    loadBufferIntoEverything(out);
  };

  const reverseAudio = () => {
    const buf = getActiveBuffer();
    if (!buf || fxPreview) return;
    pushHistory();
    const out = audioContext.createBuffer(
      buf.numberOfChannels,
      buf.length,
      buf.sampleRate
    );
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
      const src = buf.getChannelData(ch);
      const dst = out.getChannelData(ch);
      for (let i = 0, j = src.length - 1; i < src.length; i++, j--) {
        dst[i] = src[j];
      }
    }
    loadBufferIntoEverything(out);
  };

  // Normalizes to -1 dB (not 0 dB) so the audio keeps a little headroom.
  const normalizeAudio = () => {
    const buf = getActiveBuffer();
    if (!buf || fxPreview) return;
    const normalized = normalizePeak(toChannels(buf));
    if (!normalized) return; // silent audio
    pushHistory();
    loadBufferIntoEverything(toBuffer(normalized, buf.sampleRate));
  };

  // Align: move the audio later (adds silence at the start) or earlier
  // (cuts the start). Each click is one undoable edit.
  const nudge = (ms: number) => {
    const buf = getActiveBuffer();
    if (!buf || fxPreview) return;
    pushHistory();
    loadBufferIntoEverything(toBuffer(shiftAudio(toChannels(buf), buf.sampleRate, ms), buf.sampleRate));
    setShift(shiftMsRef.current + ms);
  };

  // -----------------------------------------------------------------

  const padWithSilence = (buf: AudioBuffer, preSec: number, postSec: number): AudioBuffer => {
    const sr = buf.sampleRate;
    const channels = buf.numberOfChannels;

    const preSamples = Math.max(0, Math.floor((preSec || 0) * sr));
    const postSamples = Math.max(0, Math.floor((postSec || 0) * sr));

    const outLen = preSamples + buf.length + postSamples;
    const out = audioContext.createBuffer(channels, outLen, sr);

    for (let ch = 0; ch < channels; ch++) {
      const dst = out.getChannelData(ch);
      dst.set(buf.getChannelData(ch), preSamples);
    }
    return out;
  };

  const applySilencePadding = () => {
    const buf = getActiveBuffer();
    if (!buf || fxPreview) return;
    pushHistory();
    const padded = padWithSilence(buf, preSilenceSec, postSilenceSec);
    loadBufferIntoEverything(padded);
    setSilenceApplied(true);
  };

  // -----------------------------------------------------------------

  // Speed now resamples with interpolation. The old version skipped or
  // repeated whole samples, which sounded rough and, for speeds like 0.75×,
  // didn't slow the audio down at all (it only appended silence).
  const applySpeedAndVolume = (audioBuffer: AudioBuffer) => {
    const resampled = resampleForSpeed(toChannels(audioBuffer), speed);
    return toBuffer(volume === 1 ? resampled : scaleChannels(resampled, volume), audioBuffer.sampleRate);
  };

  const applyPanToBuffer = (audioBuffer: AudioBuffer, pan: number): Promise<AudioBuffer> => {
    const offlineContext = new OfflineAudioContext(audioBuffer.numberOfChannels, audioBuffer.length, audioBuffer.sampleRate);
    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;
    const panNode = offlineContext.createStereoPanner();
    panNode.pan.setValueAtTime(pan, offlineContext.currentTime);
    source.connect(panNode);
    panNode.connect(offlineContext.destination);
    source.start();
    return offlineContext.startRendering();
  };

  const saveEditedWaveformAudio = () => {
    if (!wavesurfer || !wavesurfer.backend.buffer || fxPreview) return;
    const processedAudioBuffer = applySpeedAndVolume(wavesurfer.backend.buffer);
    applyPanToBuffer(processedAudioBuffer, pan).then((processedAudioBufferWithPan) => {
      const wavArrayBuffer = toWav(processedAudioBufferWithPan);
      const wavBlob = new Blob([wavArrayBuffer], { type: "audio/wav" });
      onAudioEdited(new File([wavBlob], "edited-audio.wav", { type: "audio/wav" }));
    }).catch((error) => {
      console.error("Error applying pan to audio:", error);
      toast.error("Couldn't prepare the edited audio.", "Please try again.");
    });
  };

  // ---------------- Effects: preview → A/B → apply ----------------
  const previewEffect = async (kind: EffectKind) => {
    const base = fxPreview ? fxPreview.original : getActiveBuffer();
    if (!base || fxBusy) return;
    const settings = fxSettings[kind];
    setFxBusy(kind);
    try {
      const processedChannels = await renderEffect(toChannels(base), base.sampleRate, settings);
      const processed = toBuffer(processedChannels, base.sampleRate);
      swapBuffer(processed);
      setFxPreview({ kind, settings, original: base, processed, showing: "B" });
    } catch (error) {
      console.error("Effect preview failed:", error);
      toast.error("Couldn't preview this effect.", "Your audio is unchanged.");
    } finally {
      setFxBusy(null);
    }
  };

  const showPreviewSide = (side: "A" | "B") => {
    if (!fxPreview || fxPreview.showing === side) return;
    swapBuffer(side === "A" ? fxPreview.original : fxPreview.processed);
    setFxPreview({ ...fxPreview, showing: side });
  };

  const applyEffect = () => {
    if (!fxPreview) return;
    commitHistoryEntry({ buffer: fxPreview.original, shiftMs: shiftMsRef.current });
    if (fxPreview.showing === "A") swapBuffer(fxPreview.processed);
    setFxPreview(null);
  };

  const cancelEffect = () => {
    if (!fxPreview) return;
    if (fxPreview.showing === "B") swapBuffer(fxPreview.original);
    setFxPreview(null);
  };

  const updateFx = <K extends EffectKind>(kind: K, patch: Partial<FxSettingsState[K]>) =>
    setFxSettings((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));

  // -----------------------------------------------------------------

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    audioRef: React.RefObject<HTMLAudioElement>
  ) => {
    if (audioRef.current) {
      if (event.target.checked) {
        audioRef.current.currentTime = mainAudioCurrentTime;
        audioRef.current.play();
      } else {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  };

  const handlePlayPause = () => {
    if (!wavesurfer) return;
    if (wavesurfer.isPlaying()) {
      wavesurfer.pause();
      if (withMain) pauseMain?.();
    } else if (withMain) {
      handlePlayTogether();
    } else {
      wavesurfer.play();
    }
    setIsPlaying(wavesurfer.isPlaying());
  };

  const handleStop = () => {
    if (!wavesurfer) return;
    if (withMain) reset();
    else wavesurfer.stop();
    setIsPlaying(false);
  };

  const handleWithMainChange = (on: boolean) => {
    // Turning it off mid-playback leaves the main track playing on its own
    // otherwise.
    if (!on && withMain) pauseMain?.();
    setWithMain(on);
  };

  const handlePanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPan = parseFloat(e.target.value);
    setPan(newPan);  // Update pan state dynamically
  };

  // Shortcuts work while focus is inside the editor (click anywhere in it).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest("input, textarea, select, [contenteditable='true']")) return;
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    if (e.key === " " && !target.closest("button")) {
      e.preventDefault();
      handlePlayPause();
    } else if (mod && key === "z") {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    } else if (mod && key === "y") {
      e.preventDefault();
      redo();
    } else if ((e.key === "Delete" || e.key === "Backspace") && showRegion) {
      e.preventDefault();
      deleteSelectedRegion();
    } else if (key === "l" && !mod && showRegion) {
      setLoopRegion((prev) => !prev);
    }
  };

  const editsLocked = !!fxPreview;
  const selectionLength = Math.max(0, endTrim - startTrim);
  const iconButton = "w-9 h-9 rounded-xl grid place-items-center text-xl text-black41 hover:bg-white disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed";
  const smallOutline = "rounded-full px-3.5 py-1.5 text-sm font-semibold text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed";
  const numberInput = "border border-[#D9D9D9] rounded-lg px-2 py-1 w-20 focus:outline-none focus:border-primary";
  const toolCard = "border border-gray-e8 rounded-2xl p-3.5 flex flex-col gap-2.5 bg-white min-w-0";
  const toolTitle = "font-semibold text-[15px]";
  const toolHint = "text-[13px] text-gray-70";

  const renderRange = (
    id: string,
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    display: string,
    onChange: (value: number) => void,
    disabled = false
  ) => (
    <div className="flex items-center gap-2 text-[13px]">
      <label htmlFor={id} className="w-[70px] shrink-0 font-semibold">{label}</label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 min-w-0 accent-primary"
      />
      <span className="w-[62px] text-right tabular-nums font-semibold text-black41">{display}</span>
    </div>
  );

  const renderEffectBody = (kind: EffectKind) => {
    const disabled = !!fxBusy;
    if (kind === "rumble") {
      const s = fxSettings.rumble;
      return renderRange(fieldId("rumble-cutoff"), "Cut below", s.cutoffHz, 40, 200, 5, `${s.cutoffHz} Hz`, (v) => updateFx("rumble", { cutoffHz: v }), disabled);
    }
    if (kind === "eq") {
      const s = fxSettings.eq;
      return (
        <div className="flex flex-col gap-1.5">
          {renderRange(fieldId("eq-low"), "Low", s.lowDb, -12, 12, 0.5, `${s.lowDb > 0 ? "+" : ""}${s.lowDb} dB`, (v) => updateFx("eq", { lowDb: v }), disabled)}
          {renderRange(fieldId("eq-mid"), "Mid", s.midDb, -12, 12, 0.5, `${s.midDb > 0 ? "+" : ""}${s.midDb} dB`, (v) => updateFx("eq", { midDb: v }), disabled)}
          {renderRange(fieldId("eq-high"), "High", s.highDb, -12, 12, 0.5, `${s.highDb > 0 ? "+" : ""}${s.highDb} dB`, (v) => updateFx("eq", { highDb: v }), disabled)}
        </div>
      );
    }
    if (kind === "reverb") {
      const s = fxSettings.reverb;
      return renderRange(fieldId("reverb-mix"), "Mix", s.mix, 0, 0.6, 0.01, `${Math.round(s.mix * 100)}%`, (v) => updateFx("reverb", { mix: v }), disabled);
    }
    if (kind === "echo") {
      const s = fxSettings.echo;
      return (
        <div className="flex flex-col gap-1.5">
          {renderRange(fieldId("echo-time"), "Time", s.delayMs, 50, 1000, 10, `${s.delayMs} ms`, (v) => updateFx("echo", { delayMs: v }), disabled)}
          {renderRange(fieldId("echo-feedback"), "Feedback", s.feedback, 0, 0.8, 0.01, `${Math.round(s.feedback * 100)}%`, (v) => updateFx("echo", { feedback: v }), disabled)}
          {renderRange(fieldId("echo-mix"), "Mix", s.mix, 0, 0.6, 0.01, `${Math.round(s.mix * 100)}%`, (v) => updateFx("echo", { mix: v }), disabled)}
        </div>
      );
    }
    if (kind === "gate") {
      const s = fxSettings.gate;
      return renderRange(fieldId("gate-threshold"), "Threshold", s.thresholdDb, -70, -20, 1, `${s.thresholdDb} dB`, (v) => updateFx("gate", { thresholdDb: v }), disabled);
    }
    return null; // compressor: presets only
  };

  return (
    <div
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="mt-4 bg-white border border-gray-e8 rounded-3xl p-4 768:p-5 flex flex-col gap-4 outline-none"
    >
      <div className="flex flex-wrap justify-between items-start gap-2">
        <div>
          <h3 className="text-xl font-bold">Audio editor</h3>
          <p className="text-[13px] text-gray-70">Your edits stay in your browser until you save.</p>
        </div>
        <p className="text-xs text-gray-70">
          Shortcuts: <kbd className="font-semibold">Space</kbd> play · <kbd className="font-semibold">⌘/Ctrl Z</kbd> undo · <kbd className="font-semibold">Delete</kbd> cut selection
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-gray-f6 rounded-2xl px-3 py-2">
        <span className="text-sm font-semibold shrink-0">Original recording</span>
        <AudioPlayer ref={audioRefOriginal} className="flex-1 min-w-[220px] !bg-white" />
        <label className="flex items-center gap-2 text-[13px] text-black41">
          <input
            type="checkbox"
            className="accent-primary"
            onChange={(e) => handleCheckboxChange(e, audioRefOriginal)}
          />
          Start at the main track's position
        </label>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 bg-gray-f6 rounded-2xl px-2.5 py-2">
        <button type="button" className={iconButton} onClick={handleBack} title="Back 10 seconds" aria-label="Back 10 seconds">
          <BsSkipBackward />
        </button>
        <button
          type="button"
          onClick={handlePlayPause}
          className="w-11 h-11 rounded-full bg-primary text-white grid place-items-center text-2xl hover:bg-[#526554]"
          // Distinct from the "Original recording" player's Play button.
          aria-label={isPlaying ? "Pause edited audio" : "Play edited audio"}
        >
          {isPlaying ? <CiPause1 /> : <CiPlay1 />}
        </button>
        <button type="button" className={iconButton} onClick={handleForward} title="Forward 10 seconds" aria-label="Forward 10 seconds">
          <BsSkipForward />
        </button>
        <button type="button" className={iconButton} onClick={handleStop} title="Stop and go to the start" aria-label="Stop">
          <BsStopFill />
        </button>
        <span className="px-1.5 font-bold tabular-nums" aria-live="off">
          {formatClock(position)} <span className="font-medium text-sm text-gray-70">/ {formatClock(duration)}</span>
        </span>
        <span className="w-px h-6 bg-[#dcdcdc] mx-1" aria-hidden="true" />
        <label className="flex items-center gap-2 text-[13px] font-semibold cursor-pointer select-none">
          <input
            type="checkbox"
            role="switch"
            checked={withMain}
            onChange={(e) => handleWithMainChange(e.target.checked)}
            className="peer sr-only"
          />
          <span className="relative w-[34px] h-5 rounded-full bg-[#cfd5ca] peer-checked:bg-primary transition-colors after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:after:translate-x-3.5 peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1" aria-hidden="true" />
          <span className={withMain ? "text-primary" : "text-black41"}>Play with main track</span>
        </label>
        <button
          type="button"
          className={`${iconButton} ${loopRegion && showRegion ? "!bg-white text-primary" : ""}`}
          onClick={() => setLoopRegion((prev) => !prev)}
          disabled={!showRegion}
          aria-pressed={loopRegion}
          title={showRegion ? "Loop the selection (L)" : "Select a region to loop it"}
          aria-label="Loop selection"
        >
          <BsRepeat />
        </button>
        <span className="w-px h-6 bg-[#dcdcdc] mx-1" aria-hidden="true" />
        <button type="button" className={iconButton} onClick={() => setZoom((prev) => Math.max(0, prev - 10))} title="Zoom out" aria-label="Zoom out">
          <CiZoomOut />
        </button>
        <button type="button" className={iconButton} onClick={() => setZoom((prev) => Math.min(100, prev + 10))} title="Zoom in" aria-label="Zoom in">
          <CiZoomIn />
        </button>
        <div className="flex items-center gap-1 ml-auto">
          <button type="button" className={iconButton} onClick={undo} disabled={!canUndo || editsLocked} title="Undo (⌘/Ctrl Z)" aria-label="Undo">
            <BsArrowCounterclockwise />
          </button>
          <button type="button" className={iconButton} onClick={redo} disabled={!canRedo || editsLocked} title="Redo (⇧⌘/Ctrl Z)" aria-label="Redo">
            <BsArrowClockwise />
          </button>
          <button
            type="button"
            onClick={resetTrimAndRegion}
            className="rounded-full px-3 py-1.5 text-sm font-semibold text-black41 hover:bg-white"
            title="Go back to the original recording"
          >
            Revert to original
          </button>
        </div>
      </div>

      {fxPreview && (
        <div className="flex flex-wrap items-center gap-2 bg-[#fff8ec] text-[#7a4a00] rounded-2xl px-3.5 py-2 text-sm" role="status">
          <IoAlertCircleOutline className="text-lg shrink-0" />
          <span className="mr-auto">
            Previewing <b>{EFFECT_INFO.find((e) => e.kind === fxPreview.kind)?.title}</b>. Apply or cancel it to keep editing.
          </span>
          <button type="button" onClick={cancelEffect} className="font-semibold underline underline-offset-[3px]">Cancel</button>
          <button type="button" onClick={applyEffect} className="rounded-full bg-primary text-white font-semibold px-3.5 py-1">Apply</button>
        </div>
      )}

      {/* Lanes on one shared timeline: ruler, main track, your audio, spectrogram.
          Every right-hand cell has the same width and padding, so the
          timeline, both waveforms and the spectrogram line up. */}
      <div className="border border-gray-e8 rounded-2xl overflow-hidden">
        <div className="flex flex-wrap justify-end gap-2 px-3 py-1.5 bg-[#fcfcfc] border-b border-gray-e8 text-xs">
          <span className="text-gray-70">
            {showRegion
              ? <>Selection <b className="text-primary tabular-nums">{formatClock(startTrim)} – {formatClock(endTrim)}</b> ({selectionLength.toFixed(1)} s)</>
              : "Drag on your audio to select a region"}
          </span>
        </div>
        <div className="grid grid-cols-[84px_minmax(0,1fr)] 768:grid-cols-[112px_minmax(0,1fr)]">
          <div className="border-b border-r border-gray-e8 bg-[#fcfcfc]" aria-hidden="true" />
          <div className="border-b border-gray-e8 bg-[#fcfcfc] px-2 min-w-0">
            <div ref={timelineRef} id="timeline" className="w-full" />
          </div>

          {mainTrackUrl && (
            <>
              <div className="flex flex-col justify-center gap-0.5 px-2.5 py-2 border-b border-r border-gray-e8 bg-[#fcfcfc]">
                <span className="text-xs font-bold text-black41">Main track</span>
                <span className="text-[11px] text-gray-70">
                  {mainLane.status === "ready" ? "for timing" : mainLane.status === "loading" ? "Loading…" : "Not available"}
                </span>
              </div>
              <div className="relative border-b border-gray-e8 px-2 py-1.5 h-[64px] min-w-0">
                <canvas
                  ref={mainCanvasRef}
                  onClick={handleMainLaneClick}
                  className="block w-full h-full cursor-pointer"
                  role="img"
                  aria-label="Main track waveform, on the same timeline as your audio"
                  title="Click to jump your audio to this moment"
                />
                {mainLane.status === "error" && (
                  <p className="absolute inset-0 flex items-center justify-center px-3 text-center text-xs text-gray-70">
                    The main track's waveform can't be loaded here. "Play with main track" still works.
                  </p>
                )}
              </div>
            </>
          )}

          <div className="flex flex-col justify-center gap-0.5 px-2.5 py-2 border-r border-gray-e8 bg-[#fcfcfc]">
            <span className="text-xs font-bold text-black41">Your audio</span>
            <span className="text-[11px] text-gray-70 tabular-nums">
              {sectionName ? `${sectionName} · ` : ""}{formatClock(duration)}
            </span>
          </div>
          <div className="px-2 py-2 min-w-0">
            <div ref={waveformRef} id="waveform" className="w-full" />
          </div>

          <div className="flex flex-col justify-center gap-0.5 px-2.5 py-2 border-t border-r border-gray-e8 bg-[#fcfcfc]">
            <span className="text-xs font-bold text-black41">Spectrogram</span>
            <span className="text-[11px] text-gray-70">click to jump there</span>
          </div>
          <div className="border-t border-gray-e8 px-2 min-w-0">
            <div ref={spectrogramRef} id="spectrogram" className="w-full cursor-pointer" />
          </div>
        </div>
      </div>

      {/* Tool tabs */}
      <div role="tablist" aria-label="Editing tools" className="flex flex-wrap gap-1 border-b border-gray-e8">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={fieldId(`tab-${tab.id}`)}
            aria-selected={activeTab === tab.id}
            aria-controls={fieldId(`panel-${tab.id}`)}
            onClick={() => setActiveTab(tab.id)}
            className={`-mb-px px-3.5 py-2 text-[15px] font-semibold border-b-2 ${
              activeTab === tab.id ? "text-primary border-primary" : "text-gray-70 border-transparent hover:text-black41"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={fieldId(`panel-${activeTab}`)} aria-labelledby={fieldId(`tab-${activeTab}`)}>
        {activeTab === "edit" && (
          <div className="grid 768:grid-cols-[1.4fr_1fr_1fr] gap-3">
            <div className={toolCard}>
              <span className={toolTitle}>Selection</span>
              <p className={toolHint}>Drag on the waveform, or use the button. Cuts get a tiny fade so they don't click.</p>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={toggleRegion} className={smallOutline} disabled={editsLocked}>
                  {showRegion ? "Clear selection" : "Select a region"}
                </button>
              </div>
              {showRegion && (
                <>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <label className="flex items-center gap-2">Start (s)
                      <input className={numberInput} type="number" min={0} step="0.1" value={Number(startTrim.toFixed(2))} onChange={(e) => handleTrimChange('start', parseFloat(e.target.value))} />
                    </label>
                    <label className="flex items-center gap-2">End (s)
                      <input className={numberInput} type="number" min={0} step="0.1" value={Number(endTrim.toFixed(2))} onChange={(e) => handleTrimChange('end', parseFloat(e.target.value))} />
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={trimWaveform} className={`${smallOutline} flex items-center gap-1.5`} disabled={editsLocked}>
                      <RiScissorsCutLine /> Keep selection
                    </button>
                    <button type="button" onClick={deleteSelectedRegion} className={`${smallOutline} flex items-center gap-1.5 !text-red-primary !ring-red-primary hover:!bg-red-primary/5`} disabled={editsLocked}>
                      <CiTrash className="text-lg" /> Delete selection
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className={toolCard}>
              <span className={toolTitle}>Fades</span>
              <div className="flex items-center gap-2 text-sm">
                <label htmlFor={fieldId("fade-in")} className="w-16">Fade in</label>
                <input id={fieldId("fade-in")} className={numberInput} type="number" min={0} step="0.1" value={fadeInSec} onChange={(e) => setFadeInSec(Math.max(0, parseFloat(e.target.value) || 0))} />
                <span className="text-gray-70">s</span>
              </div>
              <button type="button" onClick={applyFadeIn} className={`${smallOutline} self-start`} disabled={editsLocked}>Apply fade in</button>
              <div className="flex items-center gap-2 text-sm">
                <label htmlFor={fieldId("fade-out")} className="w-16">Fade out</label>
                <input id={fieldId("fade-out")} className={numberInput} type="number" min={0} step="0.1" value={fadeOutSec} onChange={(e) => setFadeOutSec(Math.max(0, parseFloat(e.target.value) || 0))} />
                <span className="text-gray-70">s</span>
              </div>
              <button type="button" onClick={applyFadeOut} className={`${smallOutline} self-start`} disabled={editsLocked}>Apply fade out</button>
            </div>

            <div className={toolCard}>
              <span className={toolTitle}>Quick fixes</span>
              <p className={toolHint}>One click each, and you can undo them.</p>
              <button type="button" onClick={normalizeAudio} className={`${smallOutline} self-start`} disabled={editsLocked}>Normalize to −1 dB</button>
              <button type="button" onClick={reverseAudio} className={`${smallOutline} self-start`} disabled={editsLocked}>Reverse</button>
            </div>
          </div>
        )}

        {activeTab === "align" && (
          <div className="grid 768:grid-cols-2 gap-3">
            <div className={toolCard}>
              <span className={toolTitle}>Move earlier or later</span>
              <p className={toolHint}>
                Later adds silence at the start; earlier cuts the start. Turn on "Play with main track" to check the timing.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => nudge(-100)} className={smallOutline} disabled={editsLocked}>−100 ms</button>
                <button type="button" onClick={() => nudge(-10)} className={smallOutline} disabled={editsLocked}>−10 ms</button>
                <span className="min-w-[96px] text-center font-bold tabular-nums bg-gray-f6 rounded-xl px-2 py-1.5" aria-live="polite">
                  {shiftMs > 0 ? "+" : ""}{shiftMs} ms
                </span>
                <button type="button" onClick={() => nudge(10)} className={smallOutline} disabled={editsLocked}>+10 ms</button>
                <button type="button" onClick={() => nudge(100)} className={smallOutline} disabled={editsLocked}>+100 ms</button>
              </div>
              <p className={toolHint}>Moved by {shiftMs > 0 ? "+" : ""}{shiftMs} ms since you opened the editor.</p>
            </div>

            <div className={toolCard}>
              <span className={toolTitle}>Silence</span>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <label className="flex items-center gap-2">Before (s)
                  <input
                    className={numberInput}
                    type="number"
                    min={0}
                    step="0.1"
                    value={preSilenceSec}
                    onChange={(e) => { setPreSilenceSec(Math.max(0, parseFloat(e.target.value || "0"))); setSilenceApplied(false); }}
                  />
                </label>
                <label className="flex items-center gap-2">After (s)
                  <input
                    className={numberInput}
                    type="number"
                    min={0}
                    step="0.1"
                    value={postSilenceSec}
                    onChange={(e) => { setPostSilenceSec(Math.max(0, parseFloat(e.target.value || "0"))); setSilenceApplied(false); }}
                  />
                </label>
              </div>
              <button type="button" onClick={applySilencePadding} className={`${smallOutline} self-start`} disabled={editsLocked}>
                {silenceApplied ? "Silence added" : "Add silence"}
              </button>
            </div>
          </div>
        )}

        {activeTab === "levels" && (
          <div className={`${toolCard} max-w-[560px]`}>
            <p className={toolHint}>You hear these right away; they're applied when you save.</p>
            <div className="flex items-center gap-2 text-[13px]">
              <button type="button" onClick={toggleMute} className="w-[70px] shrink-0 flex items-center gap-1 font-semibold" aria-label={volume === 0 ? "Unmute" : "Mute"}>
                {volume === 0 ? <CiVolumeMute className="text-xl" /> : <CiVolumeHigh className="text-xl" />} Volume
              </button>
              <input type="range" min="0" max="1" step="0.05" value={volume} onChange={handleVolumeChange} className="flex-1 min-w-0 accent-primary" aria-label="Volume" />
              <span className="w-[62px] text-right tabular-nums font-semibold text-black41">{Math.round(volume * 100)}%</span>
            </div>
            <div className="flex items-center gap-2 text-[13px]">
              <label htmlFor={fieldId("pan")} className="w-[70px] shrink-0 font-semibold">Pan</label>
              <span className="text-gray-70">L</span>
              <input id={fieldId("pan")} type="range" min="-1" max="1" step="0.01" value={pan} onChange={handlePanChange} className="flex-1 min-w-0 accent-primary" />
              <span className="text-gray-70">R</span>
              <span className="w-[50px] text-right tabular-nums font-semibold text-black41">
                {pan === 0 ? "Center" : `${pan < 0 ? "L" : "R"} ${Math.round(Math.abs(pan) * 100)}`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[13px]">
              <label htmlFor={fieldId("speed")} className="w-[70px] shrink-0 font-semibold">Speed</label>
              <input id={fieldId("speed")} type="range" min="0.5" max="2" step="0.25" value={speed} onChange={handleSpeedChange} className="flex-1 min-w-0 accent-primary" />
              <span className="w-[62px] text-right tabular-nums font-semibold text-black41">{speed}×</span>
            </div>
            {speed !== 1 && (
              <div className="flex gap-2 items-start bg-[#fff8ec] text-[#7a4a00] rounded-xl px-3 py-2 text-[13px]">
                <IoAlertCircleOutline className="text-base shrink-0 mt-0.5" />
                <span>Speed also changes pitch and length, so your audio may drift from the main track.</span>
              </div>
            )}
          </div>
        )}

        {activeTab === "effects" && (
          <div className="grid 600:grid-cols-2 1020:grid-cols-3 gap-3">
            {EFFECT_INFO.map(({ kind, title, description }) => {
              const presets = EFFECT_PRESETS[kind] as { label: string; settings: EffectSettings }[];
              const current = fxSettings[kind];
              const isPreviewing = fxPreview?.kind === kind;
              const isStale = isPreviewing && JSON.stringify(fxPreview!.settings) !== JSON.stringify(current);
              const busy = fxBusy === kind;
              return (
                <div key={kind} className={`${toolCard} ${isPreviewing ? "!border-primary/40 bg-[#fbfcfa]" : ""}`}>
                  <span className={toolTitle}>{title}</span>
                  <p className={toolHint}>{description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {presets.map((preset) => {
                      const on = JSON.stringify(preset.settings) === JSON.stringify(current);
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => setFxSettings((prev) => ({ ...prev, [kind]: preset.settings }))}
                          aria-pressed={on}
                          disabled={!!fxBusy}
                          className={`text-xs font-semibold px-2.5 py-1 rounded-full ${on ? "bg-primary text-white" : "bg-gray-f6 text-black41 hover:bg-gray-e8"}`}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                  {renderEffectBody(kind)}
                  <div className="flex flex-wrap items-center justify-end gap-2 mt-auto pt-1">
                    {isPreviewing && (
                      <div className="flex gap-0.5 bg-gray-f6 rounded-full p-0.5 mr-auto" role="group" aria-label="Compare">
                        {(["A", "B"] as const).map((side) => (
                          <button
                            key={side}
                            type="button"
                            onClick={() => showPreviewSide(side)}
                            aria-pressed={fxPreview!.showing === side}
                            className={`text-xs font-bold px-2.5 py-1 rounded-full ${fxPreview!.showing === side ? "bg-white text-primary shadow" : "text-black41"}`}
                          >
                            {side === "A" ? "A · Original" : "B · With effect"}
                          </button>
                        ))}
                      </div>
                    )}
                    {isPreviewing ? (
                      <>
                        <button type="button" onClick={cancelEffect} className="rounded-full px-3 py-1.5 text-sm font-semibold text-black41 hover:bg-gray-f6">Cancel</button>
                        {isStale && (
                          <button type="button" onClick={() => previewEffect(kind)} className={smallOutline} disabled={!!fxBusy}>
                            {busy ? "Rendering…" : "Update preview"}
                          </button>
                        )}
                        <button type="button" onClick={applyEffect} className="rounded-full px-3.5 py-1.5 text-sm font-semibold bg-primary text-white hover:bg-[#526554] disabled:opacity-50" disabled={!!fxBusy || isStale}>
                          Apply
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => previewEffect(kind)} className={smallOutline} disabled={!!fxBusy || (!!fxPreview && !isPreviewing)}>
                        {busy ? "Rendering…" : "Preview"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-e8 pt-3.5">
        {fxPreview && (
          <span className="text-[13px] text-gray-70">Apply or cancel the effect preview to save.</span>
        )}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {onCancel && (
            <Button type={ButtonTypes.CANCEL} htmlType="button" onClick={onCancel} className="!px-4 !py-2 font-semibold" disabled={isSaving}>
              Cancel
            </Button>
          )}
          {onSubmitOriginal && (
            <Button
              type={ButtonTypes.DEFAULT}
              htmlType="button"
              onClick={onSubmitOriginal}
              className="px-4 py-2 font-semibold text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary/5 disabled:opacity-50"
              disabled={isSaving}
            >
              Add original audio
            </Button>
          )}
          <Button
            type={ButtonTypes.PRIMARY}
            htmlType="button"
            onClick={saveEditedWaveformAudio}
            className="!px-5 !py-2 font-semibold"
            disabled={isSaving || editsLocked}
          >
            {isSaving ? "Saving…" : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AudioEditor;
